// Profile Editor - Beacons-style single scrollable page
// Section-based editing with view/edit toggles

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext.jsx';
import { doc, setDoc } from 'firebase/firestore';
import { getInfluencerByUserId, createOrUpdateInfluencerProfile } from '../../../lib/models/influencer';
import { triggerProfilePropagation } from '../../../lib/profile/triggerProfilePropagation';
import { uploadUserImage } from '../../../lib/uploadProfileImage';
import { logStorageError } from '../../../lib/firebase/logStorageError';
import DashboardShell from '../../../components/layout/DashboardShell';
import IdentitySection from '../../../components/profile/IdentitySection';
import ProfileBioSectionBeacons from '../../../components/profile/ProfileBioSectionBeacons';
import PlatformPerformanceSection from '../../../components/profile/PlatformPerformanceSection';
import AudienceSection from '../../../components/profile/AudienceSection';
import BrandPartnersSection from '../../../components/profile/BrandPartnersSection';
import CollaborationHighlightsSection from '../../../components/profile/CollaborationHighlightsSection';
import FeaturedPostsSection from '../../../components/profile/FeaturedPostsSection';
import SectionsModal from '../../../components/profile/SectionsModal';
import ShareMediaKitButton from '../../../components/profile/ShareMediaKitButton';
import { normalizeFeaturedPosts } from '../../../lib/featuredPosts/normalizeFeaturedPosts';
import { ensureTikTokThumbnailsCached } from '../../../lib/featuredPosts/ensureTikTokThumbnailsCached';
import { useCurrentUserDoc } from '../../../lib/useCurrentUserDoc';
import { db } from '../../../../firebaseConfig.js';
import { getCanonicalSocials, getConnectedPlatforms } from '../../../lib/socials';
import { getPlatformsFromProfile } from '../../../lib/social/utils/socialLinks';
import ScraperLoadingModal from '../../../components/profile/ScraperLoadingModal';
import DailyLimitModal from '../../../components/profile/DailyLimitModal';

const ProfileEditor = ({ onNavigate }) => {
  const { user, profile } = useAuth();
  const { userDoc, loading: userDocLoading, error: userDocError } = useCurrentUserDoc();
  const hasInitializedFromUserDoc = useRef(false);
  const hasSyncedOnboardingSocials = useRef(false);
  const hasAppliedOnboardingSocials = useRef(false);
  const hasRefetchedForTiktokPending = useRef(false);
  const hasRefetchedForInstagramPending = useRef(false);
  const canonicalSocials = useMemo(() => getCanonicalSocials(userDoc), [userDoc]);
  const connectedPlatforms = useMemo(
    () => getConnectedPlatforms(canonicalSocials),
    [canonicalSocials]
  );

  const buildPrimarySocialLinks = (socials) => {
    if (!Array.isArray(socials)) return {};
    const primary = socials.find((social) => social?.isPrimary) || socials[0];
    if (!primary?.platform || !primary?.url) return {};
    return { [primary.platform]: primary.url };
  };

  /** Build socialLinks map from all socials (so TikTok and Instagram both get refreshed when both are present). */
  const buildAllSocialLinks = (socials) => {
    if (!Array.isArray(socials)) return {};
    return socials.reduce((acc, s) => {
      if (s?.platform && s?.url) acc[s.platform] = s.url;
      return acc;
    }, {});
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rateLimitBanner, setRateLimitBanner] = useState(null);
  const [scraperLoading, setScraperLoading] = useState(false);
  const [scraperDone, setScraperDone] = useState(false);
  const [scraperPlatform, setScraperPlatform] = useState('');
  const [dailyLimitModal, setDailyLimitModal] = useState({ open: false, nextAllowedAtISO: null, platform: '' });

  // Form state - matches InfluencerProfile structure
  const [formData, setFormData] = useState({
    username: '',
    displayName: '',
    bio: '',
    niche: [],
    profileImageUrl: '',
    bannerImageUrl: '',
    platforms: [],
    stats: {
      totalFollowers: 0,
      engagementRate: 0,
    },
    platformStats: {},
    audience: {},
    links: [],
    collaborations: [],
    portfolio: [],
    featuredVideos: [],
    featuredPosts: [],
    brandPartners: [],
    location: '',
    contactEmail: '',
    sectionsEnabled: {},
    identity: {}, // Ensure identity object exists
    socialAccounts: {},
  });

  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [showSectionsModal, setShowSectionsModal] = useState(false);

  // Load profile function - defined outside useEffect so it can be reused
  // Pass { fromServer: true } after API writes (e.g. TikTok refresh) to avoid stale cache
  const loadProfile = async (options = {}) => {
    if (!user || !profile || (profile.userType && profile.userType !== 'influencer')) {
      if (!userDocLoading && !hasInitializedFromUserDoc.current) {
        setLoading(false);
      }
      return;
    }

      try {
        // Try to load from new influencers collection
        let influencer = null;
        try {
          influencer = await getInfluencerByUserId(user.uid, options);
        } catch (err) {
          console.log('Profile not found in new collection, using legacy data:', err);
        }

        if (influencer) {
          // Use new influencer profile data
          // Prefer identity object if it exists, otherwise fall back to top-level fields
          const identity = influencer.identity || {};
          // Merge identity with top-level fields to ensure we have all data
          const mergedIdentity = {
            displayName: identity.displayName || influencer.displayName || '',
            handle: identity.handle || influencer.username || '',
            creatorTitle: identity.creatorTitle || influencer.creatorTitle || '',
            // Handle niche as comma-separated string or array
            subtitle: identity.subtitle || (Array.isArray(influencer.niche) ? influencer.niche.join(', ') : (influencer.niche || '')),
            location: identity.location || influencer.location || '',
            primaryPlatforms: identity.primaryPlatforms || influencer.platforms?.map(p => p.platformName) || [],
            totalFollowersOverride: identity.totalFollowersOverride ?? influencer.stats?.totalFollowersOverride,
          };
          // Ensure username is synced from identity.handle if username is missing
          const usernameToUse = influencer.username || mergedIdentity.handle || profile?.username || profile?.usernameLower || userDoc?.username || userDoc?.usernameLower || '';
          
          // Always ensure username is synced from identity.handle for consistent lookups
          if (mergedIdentity.handle) {
            const normalizedHandle = mergedIdentity.handle.toLowerCase().trim();
            const currentUsername = influencer.username?.toLowerCase().trim();
            
            // Sync if username is missing or doesn't match handle
            if (!currentUsername || currentUsername !== normalizedHandle) {
              console.log('[ProfileEdit] Syncing username from handle:', {
                handle: mergedIdentity.handle,
                normalizedHandle,
                currentUsername,
                willSync: true
              });
              // Silently sync username in the background
              createOrUpdateInfluencerProfile(user.uid, { 
                username: normalizedHandle,
                identity: {
                  ...influencer.identity,
                  handle: mergedIdentity.handle, // Ensure handle is also saved
                }
              }).catch(err => {
                console.error('[ProfileEdit] Error syncing username:', err);
              });
            }
          }
          
          // Prefer influencer.contactEmail if set; otherwise fall back to auth/profile email
          const fallbackEmail = influencer.contactEmail || profile.email || user.email || '';
          const onboardingPhoto = userDoc?.photoURL || userDoc?.avatarUrl || userDoc?.profileImageUrl || '';

          const socialLinksFromOnboarding = buildPrimarySocialLinks(userDoc?.socials);
          const primaryPlatformFromOnboarding = Array.isArray(userDoc?.socials)
            ? (userDoc.socials.find((social) => social?.isPrimary) || userDoc.socials[0])?.platform
            : null;
          const primaryPlatformsFallback =
            (mergedIdentity.primaryPlatforms && mergedIdentity.primaryPlatforms.length > 0)
              ? mergedIdentity.primaryPlatforms
              : (formData.identity?.primaryPlatforms && formData.identity.primaryPlatforms.length > 0)
                ? formData.identity.primaryPlatforms
                : (primaryPlatformFromOnboarding ? [primaryPlatformFromOnboarding] : []);

          const serverSocialAccounts = influencer.socialAccounts || {};
          // Apply any scraped data passed from onboarding so Platform Performance shows immediately without refresh
          let onboardingInstagram = null;
          let onboardingTiktok = null;
          try {
            const raw = sessionStorage.getItem('influencehub_onboarding_instagram');
            if (raw) {
              onboardingInstagram = JSON.parse(raw);
              sessionStorage.removeItem('influencehub_onboarding_instagram');
            }
          } catch (_) {}
          try {
            const raw = sessionStorage.getItem('influencehub_onboarding_tiktok');
            if (raw) {
              onboardingTiktok = JSON.parse(raw);
              sessionStorage.removeItem('influencehub_onboarding_tiktok');
            }
          } catch (_) {}

          setFormData((prev) => {
            // Merge: server overwrites only for keys it has; prefer prev when client has real stats and server doesn't (add/edit flow or stale read)
            const merged = { ...(prev.socialAccounts || {}) };
            for (const k of Object.keys(serverSocialAccounts)) {
              const serverVal = serverSocialAccounts[k];
              const prevVal = prev.socialAccounts?.[k];
              if (serverVal == null) {
                if (prevVal && (prevVal.derived != null || prevVal.lastRefreshedAt))
                  merged[k] = prevVal;
                else
                  merged[k] = serverVal;
              } else if (prevVal && (prevVal.derived != null || prevVal.lastRefreshedAt) && (serverVal.derived == null || !serverVal.lastRefreshedAt)) {
                // Keep client data when server has no/empty derived (stale read or second platform just added)
                merged[k] = prevVal;
              } else {
                merged[k] = serverVal;
              }
            }
            if (onboardingInstagram) merged.instagram = onboardingInstagram;
            if (onboardingTiktok) merged.tiktok = onboardingTiktok;
            const mergedSocialAccounts = merged;
            return {
              username: usernameToUse || userDoc?.username || userDoc?.usernameLower || '',
              displayName: mergedIdentity.displayName || '',
              creatorTitle: mergedIdentity.creatorTitle || '',
              bio: influencer.bio || '',
              niche: mergedIdentity.subtitle ? [mergedIdentity.subtitle] : (Array.isArray(influencer.niche) ? influencer.niche : (influencer.niche ? [influencer.niche] : [])),
              profileImageUrl: influencer.profileImageUrl || onboardingPhoto || '',
              photoURL: influencer.photoURL || onboardingPhoto || '',
              avatarUrl: influencer.avatarUrl || onboardingPhoto || '',
              bannerImageUrl: influencer.bannerImageUrl || '',
              platforms: influencer.platforms || [],
              stats: influencer.stats || { totalFollowers: 0, engagementRate: 0 },
              platformStats: influencer.platformStats || {},
              audience: influencer.audience || {},
              identity: {
                ...mergedIdentity,
                handle: mergedIdentity.handle || usernameToUse || userDoc?.username || userDoc?.usernameLower || '',
                primaryPlatforms: primaryPlatformsFallback,
              },
              links: influencer.links || [],
              socialLinks: influencer.socialLinks || prev.socialLinks || socialLinksFromOnboarding || {},
              collaborations: influencer.collaborations || [],
              portfolio: influencer.portfolio || [],
              featuredVideos: influencer.featuredVideos || [],
              featuredPosts: normalizeFeaturedPosts(influencer.featuredPosts),
              brandPartners: influencer.brandPartners || [],
              location: mergedIdentity.location || '',
              contactEmail: fallbackEmail,
              sectionsEnabled: influencer.sectionsEnabled || {},
              instagram: influencer.instagram || null,
              socialAccounts: mergedSocialAccounts,
            };
          });
          setProfileImagePreview(
            influencer.profileImageUrl ||
            influencer.photoURL ||
            influencer.avatarUrl ||
            onboardingPhoto ||
            profileImagePreview ||
            null
          );
          
          // Backfill thumbnails for legacy posts (rate limited, runs in background)
          const normalizedPosts = normalizeFeaturedPosts(influencer.featuredPosts);
          
          // CRITICAL: Ensure all profile data is saved to Firestore
          // This fixes the issue where the profile was created with only 7 fields
          // and subsequent partial updates don't include all fields
          console.log('[ProfileEditor] Ensuring complete profile data is saved to Firestore...');
          
          // Build identity object without undefined values
          const cleanIdentity = {
            displayName: mergedIdentity.displayName || '',
            handle: mergedIdentity.handle || usernameToUse,
          };
          if (mergedIdentity.creatorTitle) {
            cleanIdentity.creatorTitle = mergedIdentity.creatorTitle;
          }
          if (mergedIdentity.subtitle) {
            cleanIdentity.subtitle = mergedIdentity.subtitle;
          }
          if (mergedIdentity.location) {
            cleanIdentity.location = mergedIdentity.location;
          }
          if (mergedIdentity.primaryPlatforms && Array.isArray(mergedIdentity.primaryPlatforms) && mergedIdentity.primaryPlatforms.length > 0) {
            cleanIdentity.primaryPlatforms = mergedIdentity.primaryPlatforms;
          }
          // Explicitly exclude totalFollowersOverride (deprecated field)
          
          const socialLinksFromOnboardingForSave = buildPrimarySocialLinks(userDoc?.socials);
          const completeProfileData = {
            // Identity fields
            displayName: mergedIdentity.displayName || influencer.displayName || '',
            username: usernameToUse,
            creatorTitle: mergedIdentity.creatorTitle || influencer.creatorTitle || null,
            // Bio
            bio: influencer.bio || formData.bio || '',
            // Avatar fields (save all variations for compatibility)
            profileImageUrl: influencer.profileImageUrl || formData.profileImageUrl || onboardingPhoto || '',
            photoURL: influencer.photoURL || formData.photoURL || onboardingPhoto || '',
            avatarUrl: influencer.avatarUrl || formData.avatarUrl || onboardingPhoto || '',
            // Banner
            bannerImageUrl: influencer.bannerImageUrl || formData.bannerImageUrl || '',
            // Location and contact
            location: mergedIdentity.location || influencer.location || formData.location || '',
            contactEmail: fallbackEmail || formData.contactEmail || '',
            // Niche
            niche: mergedIdentity.subtitle ? [mergedIdentity.subtitle] : (Array.isArray(influencer.niche) ? influencer.niche : (influencer.niche ? [influencer.niche] : [])),
            niches: mergedIdentity.subtitle ? [mergedIdentity.subtitle] : (Array.isArray(influencer.niche) ? influencer.niche : (influencer.niche ? [influencer.niche] : [])),
            // Platform stats
            platformStats: influencer.platformStats || formData.platformStats || {},
            // Social links
            socialLinks: influencer.socialLinks || formData.socialLinks || socialLinksFromOnboardingForSave || {},
            // Other fields
            audience: influencer.audience || formData.audience || {},
            brandPartners: influencer.brandPartners || formData.brandPartners || [],
            collaborations: influencer.collaborations || formData.collaborations || [],
            featuredPosts: normalizedPosts,
            sectionsEnabled: influencer.sectionsEnabled || formData.sectionsEnabled || {},
            // Identity object (cleaned, no undefined values)
            identity: cleanIdentity,
          };
          
          // Remove undefined values from completeProfileData before saving
          const cleanedProfileData = {};
          Object.keys(completeProfileData).forEach(key => {
            const value = completeProfileData[key];
            if (value !== undefined) {
              // For nested objects, also clean them
              if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)) {
                const cleanedNested = {};
                Object.keys(value).forEach(nestedKey => {
                  if (value[nestedKey] !== undefined) {
                    cleanedNested[nestedKey] = value[nestedKey];
                  }
                });
                // Only include nested object if it has at least one key
                if (Object.keys(cleanedNested).length > 0) {
                  cleanedProfileData[key] = cleanedNested;
                }
              } else {
                cleanedProfileData[key] = value;
              }
            }
          });
          
          // Save complete profile data to Firestore (this will merge with existing data)
          createOrUpdateInfluencerProfile(user.uid, cleanedProfileData).then(() => {
            console.log('[ProfileEditor] Complete profile data saved successfully');
          }).catch(err => {
            console.error('[ProfileEditor] Error saving complete profile data:', err);
            // Don't show error to user - this is a background sync
          });
          
          if (normalizedPosts.length > 0) {
            ensureTikTokThumbnailsCached(user.uid, normalizedPosts).catch((err) => {
              console.warn('[ProfileEdit] Failed to backfill thumbnails:', err);
            });
          }
        } else {
          // Fallback: Migrate profile from users collection to influencers collection
          console.log('[ProfileEdit] Profile not found in influencers collection, migrating from users collection');
          
          const handle = profile.handle || '';
          const displayName = profile.displayName || (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : '') || '';
          const normalizedHandle = handle.toLowerCase().trim();
          
          // Build identity object from legacy profile data
          const identity = {
            displayName: displayName,
            handle: handle,
            subtitle: profile.profile?.audience?.niches?.join(', ') || '',
            location: '',
            primaryPlatforms: [],
          };
          
          // Create profile in influencers collection with migrated data
          const migratedContactEmail = profile.email || user.email || '';

          const migratedProfileData = {
            username: normalizedHandle, // CRITICAL: Set username for public profile lookups
            displayName: displayName,
            bio: profile.profile?.bio?.text || '',
            niche: profile.profile?.audience?.niches || [],
            profileImageUrl: profile.photoUrl || profile.profile?.media?.profilePictureUrl || '',
            platforms: [],
            stats: {
              totalFollowers: profile.profile?.audience?.totalFollowers || 0,
              engagementRate: profile.profile?.audience?.engagementRate || 0,
            },
            platformStats: profile.profile?.platformStats || {},
            audience: profile.profile?.audience || {},
            identity: identity, // Include identity object with handle
            links: [],
            socialLinks: {},
            collaborations: [],
            portfolio: [],
            featuredVideos: [],
            featuredPosts: [],
            brandPartners: [],
            location: '',
            contactEmail: migratedContactEmail,
            sectionsEnabled: {},
          };
          
          // Migrate to influencers collection
          try {
            console.log('[ProfileEdit] Creating profile in influencers collection with handle:', normalizedHandle);
            console.log('[ProfileEdit] Migration data:', {
              username: migratedProfileData.username,
              identityHandle: migratedProfileData.identity?.handle,
              displayName: migratedProfileData.displayName,
            });
            await createOrUpdateInfluencerProfile(user.uid, migratedProfileData);
            console.log('[ProfileEdit] Profile migrated successfully');
            
            // Verify the profile was created and can be found by username
            if (normalizedHandle) {
              const { getInfluencerByUsername } = await import('../../../lib/models/influencer');
              const verifiedProfile = await getInfluencerByUsername(normalizedHandle);
              if (verifiedProfile) {
                console.log('[ProfileEdit] ✅ Profile verified - can be found by username:', {
                  userId: verifiedProfile.userId,
                  username: verifiedProfile.username,
                  identityHandle: verifiedProfile.identity?.handle,
                });
              } else {
                console.warn('[ProfileEdit] ⚠️ Profile migrated but cannot be found by username. This may be a timing issue.');
              }
            }
          } catch (migrationError) {
            console.error('[ProfileEdit] Error migrating profile:', migrationError);
            setError('Failed to migrate profile. Please refresh the page.');
          }
          
          setFormData(migratedProfileData);
          setProfileImagePreview(profile.photoUrl || profile.profile?.media?.profilePictureUrl || null);
        }
        setError(null);
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile. Please refresh the page.');
      } finally {
        setLoading(false);
      }
  };

  // Prefill from users doc once
  useEffect(() => {
    if (!userDoc || hasInitializedFromUserDoc.current) return;

    const primarySocial = Array.isArray(userDoc.socials)
      ? userDoc.socials.find((social) => social?.isPrimary) || userDoc.socials[0]
      : null;
    const socialLinksFromOnboarding = buildPrimarySocialLinks(userDoc.socials);

    const normalizedNiche = Array.isArray(userDoc.niche)
      ? userDoc.niche
      : (userDoc.niche ? [userDoc.niche] : []);

    setFormData((prev) => ({
      ...prev,
      username: userDoc.username || userDoc.usernameLower || prev.username,
      displayName: userDoc.displayName || prev.displayName,
      bio: userDoc.bio || userDoc.description || prev.bio,
      niche: normalizedNiche.length > 0 ? normalizedNiche : prev.niche,
      contactEmail: userDoc.contactEmail || prev.contactEmail || user?.email || '',
      profileImageUrl: userDoc.photoURL || userDoc.profileImageUrl || userDoc.avatarUrl || prev.profileImageUrl,
      photoURL: userDoc.photoURL || userDoc.profileImageUrl || userDoc.avatarUrl || prev.photoURL || '',
      avatarUrl: userDoc.avatarUrl || userDoc.photoURL || prev.avatarUrl || '',
      socialLinks: Object.keys(socialLinksFromOnboarding).length > 0
        ? socialLinksFromOnboarding
        : (prev.socialLinks || {}),
      identity: {
        ...prev.identity,
        displayName: userDoc.displayName || prev.identity?.displayName || '',
        handle: userDoc.username || userDoc.usernameLower || prev.identity?.handle || '',
        primaryPlatforms: primarySocial?.platform ? [primarySocial.platform] : prev.identity?.primaryPlatforms || [],
      },
    }));

    if (userDoc.photoURL || userDoc.profileImageUrl || userDoc.avatarUrl) {
      setProfileImagePreview(userDoc.photoURL || userDoc.profileImageUrl || userDoc.avatarUrl);
    }

    hasInitializedFromUserDoc.current = true;
  }, [userDoc, user?.email]);

  // Apply onboarding Instagram/TikTok from sessionStorage as soon as we have userDoc so Platform
  // Performance shows on first paint when arriving from onboarding (avoids needing a refresh).
  useEffect(() => {
    if (!user?.uid || !userDoc || hasAppliedOnboardingSocials.current) return;
    let onboardingInstagram = null;
    let onboardingTiktok = null;
    try {
      const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('influencehub_onboarding_instagram') : null;
      if (raw) {
        onboardingInstagram = JSON.parse(raw);
      }
    } catch (_) {}
    try {
      const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('influencehub_onboarding_tiktok') : null;
      if (raw) {
        onboardingTiktok = JSON.parse(raw);
      }
    } catch (_) {}
    if (!onboardingInstagram && !onboardingTiktok) return;
    hasAppliedOnboardingSocials.current = true;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('influencehub_onboarding_instagram');
      sessionStorage.removeItem('influencehub_onboarding_tiktok');
    }
    setFormData((prev) => ({
      ...prev,
      socialAccounts: {
        ...(prev.socialAccounts || {}),
        ...(onboardingInstagram && { instagram: onboardingInstagram }),
        ...(onboardingTiktok && { tiktok: onboardingTiktok }),
      },
    }));
  }, [user?.uid, userDoc]);

  useEffect(() => {
    if (!userDoc || !user?.uid) return;

    const onboardingHandle = userDoc.username || userDoc.usernameLower || '';
    if (!onboardingHandle) return;

    setFormData((prev) => {
      const currentHandle = prev.identity?.handle || prev.username;
      if (currentHandle) {
        return prev;
      }
      return {
        ...prev,
        username: onboardingHandle,
        identity: {
          ...prev.identity,
          handle: onboardingHandle,
        },
      };
    });
  }, [userDoc, user?.uid]);

  useEffect(() => {
    if (!userDoc || !user?.uid || hasSyncedOnboardingSocials.current) return;

    const socialLinksFromOnboarding = buildPrimarySocialLinks(userDoc.socials);
    const primaryPlatformFromOnboarding = Array.isArray(userDoc.socials)
      ? (userDoc.socials.find((social) => social?.isPrimary) || userDoc.socials[0])?.platform
      : null;

    if (Object.keys(socialLinksFromOnboarding).length === 0 || !primaryPlatformFromOnboarding) {
      hasSyncedOnboardingSocials.current = true;
      return;
    }

    hasSyncedOnboardingSocials.current = true;

    createOrUpdateInfluencerProfile(user.uid, {
      socialLinks: socialLinksFromOnboarding,
      identity: {
        primaryPlatforms: [primaryPlatformFromOnboarding],
      },
    }).catch((err) => {
      console.warn('[ProfileEdit] Failed to sync onboarding socials', err);
    });
  }, [userDoc, user?.uid]);

  useEffect(() => {
    if (!userDoc) return;

    const onboardingPhoto =
      userDoc.photoURL || userDoc.profileImageUrl || userDoc.avatarUrl || '';

    if (!onboardingPhoto) return;

    setFormData((prev) => {
      if (prev.photoURL || prev.profileImageUrl || prev.avatarUrl) {
        return prev;
      }
      return {
        ...prev,
        profileImageUrl: onboardingPhoto,
        photoURL: onboardingPhoto,
        avatarUrl: onboardingPhoto,
      };
    });

    if (!profileImagePreview) {
      setProfileImagePreview(onboardingPhoto);
    }
  }, [userDoc, profileImagePreview]);

  function getInstagramHandleFromLink(url) {
    if (!url || typeof url !== 'string') return null;
    const t = url.trim();
    const m = t.match(/instagram\.com\/([^/?#]+)/i);
    return m ? m[1].replace(/^@/, '').trim().toLowerCase() : t.replace(/^@/, '').trim().toLowerCase();
  }

  /** Normalize TikTok URL or handle for comparison (so we only run scraper when handle actually changed). */
  function getTikTokHandleFromLink(url) {
    if (!url || typeof url !== 'string') return null;
    const t = url.trim();
    if (t.includes('tiktok.com')) {
      try {
        const parsed = new URL(t.startsWith('http') ? t : `https://${t}`);
        const match = parsed.pathname?.match(/@([^/?#]+)/);
        return match ? match[1].trim().toLowerCase() : null;
      } catch {
        return t.replace(/^@/, '').trim().toLowerCase();
      }
    }
    return t.replace(/^@/, '').trim().toLowerCase() || null;
  }

  /** True if this platform was added or its handle/URL changed (so we should run only that platform's scraper). */
  function platformAddedOrChanged(platform, nextLinks, prevLinks) {
    const prev = prevLinks || {};
    const next = nextLinks || {};
    if (platform === 'tiktok') {
      const prevNorm = getTikTokHandleFromLink(prev.tiktok);
      const nextNorm = getTikTokHandleFromLink(next.tiktok);
      return !!next.tiktok && (prevNorm !== nextNorm);
    }
    if (platform === 'instagram') {
      const prevNorm = getInstagramHandleFromLink(prev.instagram);
      const nextNorm = getInstagramHandleFromLink(next.instagram);
      return !!next.instagram && (prevNorm !== nextNorm);
    }
    return false;
  }

  function buildInstagramStub(handle) {
    if (!handle) return null;
    return {
      handle,
      profileUrl: `https://www.instagram.com/${handle}/`,
      followersCount: null,
      lastRefreshedAt: null,
      pendingRefresh: true,
      derived: null,
      posts: [],
      rawRef: null,
    };
  }

  // When user adds/edits/removes socials via SocialMediaConnect: sync to influencer profile and trigger scrapers only for platforms that were added or changed. One platform per save.
  const handleSocialsChange = async (nextSocials) => {
    if (!user?.uid || !Array.isArray(nextSocials)) return;
    const prevSocialLinks = formData.socialLinks || {};
    const socialLinks = buildAllSocialLinks(nextSocials);
    const tiktokAddedOrChanged = platformAddedOrChanged('tiktok', socialLinks, prevSocialLinks);
    const instagramAddedOrChanged = platformAddedOrChanged('instagram', socialLinks, prevSocialLinks);
    if (tiktokAddedOrChanged && instagramAddedOrChanged) {
      setError('Please add or update one platform at a time. Save one, then add the other.');
      return;
    }
    const updateData = { socialLinks };
    if (!socialLinks.tiktok) {
      updateData.socialAccounts = { ...(formData.socialAccounts || {}), tiktok: null };
    }
    if (socialLinks.tiktok) {
      updateData.socialConsent = { publicData: true };
    }
    const igHandle = getInstagramHandleFromLink(socialLinks.instagram);
    if (igHandle) {
      const existing = formData.socialAccounts?.instagram;
      const existingHandleNorm = existing ? getInstagramHandleFromLink(existing.handle || existing.profileUrl) : null;
      const instagramHandleChanged = !existing || existingHandleNorm !== igHandle;
      updateData.socialAccounts = updateData.socialAccounts || { ...(formData.socialAccounts || {}) };
      updateData.socialAccounts.instagram = existing
        ? { ...existing, handle: igHandle, profileUrl: `https://www.instagram.com/${igHandle}/`, pendingRefresh: instagramHandleChanged ? true : (existing.pendingRefresh ?? false) }
        : buildInstagramStub(igHandle);
    } else if (socialLinks.instagram === undefined || socialLinks.instagram === null || socialLinks.instagram === '') {
      updateData.socialAccounts = updateData.socialAccounts || { ...(formData.socialAccounts || {}) };
      updateData.socialAccounts.instagram = null;
    }
    // When a platform is removed, remove its featured posts too (works for any platform, including future socials)
    const allowedPlatforms = new Set(
      Object.keys(socialLinks)
        .filter((k) => socialLinks[k] && String(socialLinks[k]).trim())
        .map((k) => k.toLowerCase().trim())
    );
    const currentPosts = formData.featuredPosts || [];
    const filteredFeaturedPosts = currentPosts.filter((p) => {
      const platform = (p.platform || '').toLowerCase().replace(/^@/, '').trim();
      return platform ? allowedPlatforms.has(platform) : false;
    });
    if (filteredFeaturedPosts.length !== currentPosts.length) {
      updateData.featuredPosts = filteredFeaturedPosts;
    }
    try {
      await createOrUpdateInfluencerProfile(user.uid, updateData);
      await triggerProfilePropagation({
        uid: user.uid,
        getToken: () =>
          user && typeof user.getIdToken === 'function'
            ? user.getIdToken()
            : Promise.resolve(null),
      });
      setFormData((prev) => ({
        ...prev,
        socialLinks,
        ...(filteredFeaturedPosts.length !== currentPosts.length ? { featuredPosts: filteredFeaturedPosts } : {}),
      }));
      if (tiktokAddedOrChanged) {
        setScraperLoading(true);
        setScraperDone(false);
        setScraperPlatform('TikTok');
        try {
          const res = await fetch('/api/social/tiktok/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tiktokHandleOrUrl: socialLinks.tiktok,
              source: 'edit_profile',
              reason: 'handle_change',
              userId: user.uid,
            }),
          });
          let body = {};
          try {
            body = res.ok ? await res.json() : {};
          } catch (_) {
            body = {};
          }
          // Always show banner when API indicates daily limit (so user sees why refresh didn't run)
          if (res.ok && (body.reason === 'daily_limit' || (body.skipped && body.rateLimit))) {
            setRateLimitBanner(body.rateLimit ? { nextAllowedAtISO: body.rateLimit.nextAllowedAtISO } : { nextAllowedAtISO: '' });
            setDailyLimitModal({
              open: true,
              nextAllowedAtISO: body.rateLimit?.nextAllowedAtISO ?? null,
              platform: 'TikTok',
            });
          }
          if (res.ok && body.ok && !body.skipped) {
            setSuccess('TikTok stats updated');
            // Optimistically update form so Platform Performance shows new stats immediately
            if (body.account || body.derived) {
              setFormData((prev) => ({
                ...prev,
                socialAccounts: {
                  ...(prev.socialAccounts || {}),
                  tiktok: {
                    ...(prev.socialAccounts?.tiktok || {}),
                    handle: body.account?.handle ?? prev.socialAccounts?.tiktok?.handle,
                    profileUrl: body.account?.profileUrl ?? prev.socialAccounts?.tiktok?.profileUrl,
                    followersCount: body.account?.followersCount ?? prev.socialAccounts?.tiktok?.followersCount,
                    derived: body.derived ?? prev.socialAccounts?.tiktok?.derived,
                    lastRefreshedAt: new Date().toISOString(),
                  },
                },
              }));
            }
            // Do NOT refetch here — delayed loadProfile can get stale doc and overwrite the new platform's stats; we already have the data from body
          } else if (res.ok && body.skipped && body.reason === 'daily_limit') {
            setSuccess('Saved.');
            setTimeout(() => loadProfile({ fromServer: true }), 1200);
          } else if (res.ok && body.skipped) {
            const at = body.nextAllowedAt ? new Date(body.nextAllowedAt).toLocaleString() : 'later';
            setSuccess(body.reason === 'cooldown_handle_changed' ? `Handle updated; next refresh at ${at}` : `TikTok stats can be refreshed again at ${at}`);
            setTimeout(() => loadProfile({ fromServer: true }), 1200);
          } else {
            setSuccess(res.ok ? 'Saved. Refreshing stats…' : 'Saved. TikTok refresh failed—try again later.');
            setTimeout(() => loadProfile({ fromServer: true }), 1200);
            if (res.ok) setTimeout(() => loadProfile({ fromServer: true }), 2500);
          }
        } catch (tiktokErr) {
          console.warn('[ProfileEdit] TikTok refresh failed (non-blocking):', tiktokErr);
          setSuccess('Saved handle; TikTok refresh failed. Try again later.');
          setTimeout(() => loadProfile({ fromServer: true }), 1200);
        } finally {
          setScraperDone(true);
        }
      }
      // Run Instagram refresh only when Instagram was added or changed (not when user only added TikTok)
      if (instagramAddedOrChanged) {
        const igHandle = getInstagramHandleFromLink(socialLinks.instagram);
        if (igHandle) {
          setSuccess(tiktokAddedOrChanged ? 'Refreshing Instagram…' : 'Social links updated. Refreshing Instagram…');
          setScraperLoading(true);
          setScraperDone(false);
          setScraperPlatform('Instagram');
          try {
            const res = await fetch('/api/social/instagram/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ handle: igHandle, userId: user.uid }),
            });
            const body = res.ok ? await res.json().catch(() => ({})) : {};
            if (res.ok && body.ok) {
              setSuccess(tiktokAddedOrChanged ? 'TikTok & Instagram stats updated' : 'Instagram stats updated');
              setFormData((prev) => ({
                ...prev,
                socialAccounts: {
                  ...(prev.socialAccounts || {}),
                  instagram: body.instagram ?? prev.socialAccounts?.instagram,
                },
              }));
              // Do NOT refetch here — a delayed loadProfile can get a stale doc (pre-write) and overwrite the metrics; we already have the data from body.instagram
            } else if (res.status === 429 && body.error === 'DAILY_LIMIT') {
              setSuccess(tiktokAddedOrChanged ? 'TikTok updated. Instagram refresh limit reached for today.' : 'Saved. Instagram refresh limit reached for today.');
              setRateLimitBanner(body.nextAllowedAtISO ? { nextAllowedAtISO: body.nextAllowedAtISO } : { nextAllowedAtISO: '' });
              setDailyLimitModal({
                open: true,
                nextAllowedAtISO: body.nextAllowedAtISO ?? null,
                platform: 'Instagram',
              });
              setTimeout(() => loadProfile({ fromServer: true }), 1200);
            } else {
              setSuccess(tiktokAddedOrChanged ? 'TikTok updated. Instagram refresh failed—try again later.' : 'Saved. Instagram refresh failed—try again later.');
              setTimeout(() => loadProfile({ fromServer: true }), 1200);
            }
          } catch (igErr) {
            console.warn('[ProfileEdit] Instagram refresh failed (non-blocking):', igErr);
            setSuccess(tiktokAddedOrChanged ? 'TikTok updated. Instagram refresh failed—try again later.' : 'Saved. Instagram refresh failed—try again later.');
            setTimeout(() => loadProfile({ fromServer: true }), 1200);
          } finally {
            setScraperDone(true);
          }
        }
      } else if (!tiktokAddedOrChanged && !instagramAddedOrChanged) {
        setSuccess('Social links updated');
        loadProfile();
      }
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      console.error('[ProfileEdit] Failed to sync socials to profile', err);
      setError('Failed to save social links to profile.');
    }
  };

  // Load profile when user or profile changes
  useEffect(() => {
    loadProfile();
  }, [user, profile]);

  // After initial load: if we have a TikTok link but no scraped data yet, refetch once from server
  // (API may have finished writing after client aborted; refetch picks up socialAccounts.tiktok)
  useEffect(() => {
    if (!user?.uid || loading || hasRefetchedForTiktokPending.current) return;
    const tiktokLink = formData.socialLinks?.tiktok || (Array.isArray(canonicalSocials) && canonicalSocials.find((s) => s?.platform === 'tiktok')?.url);
    if (!tiktokLink) return;
    const hasTiktokData = formData.socialAccounts?.tiktok?.derived != null || (formData.socialAccounts?.tiktok?.followersCount != null && formData.socialAccounts?.tiktok?.lastRefreshedAt);
    if (hasTiktokData) return;
    hasRefetchedForTiktokPending.current = true;
    const t = setTimeout(() => {
      loadProfile({ fromServer: true });
    }, 3000);
    return () => clearTimeout(t);
  }, [user?.uid, loading, formData.socialLinks, formData.socialAccounts?.tiktok, canonicalSocials]);

  // After initial load: if we have an Instagram link but no scraped data yet, refetch once from server
  // (API may have finished writing after client aborted; refetch picks up socialAccounts.instagram)
  useEffect(() => {
    if (!user?.uid || loading || hasRefetchedForInstagramPending.current) return;
    const igLink = formData.socialLinks?.instagram || (Array.isArray(canonicalSocials) && canonicalSocials.find((s) => s?.platform === 'instagram')?.url);
    if (!igLink) return;
    const hasIgData = formData.socialAccounts?.instagram?.derived != null || (formData.socialAccounts?.instagram?.followersCount != null && formData.socialAccounts?.instagram?.lastRefreshedAt);
    if (hasIgData) return;
    hasRefetchedForInstagramPending.current = true;
    const t = setTimeout(() => {
      loadProfile({ fromServer: true });
    }, 3000);
    return () => clearTimeout(t);
  }, [user?.uid, loading, formData.socialLinks, formData.socialAccounts?.instagram, canonicalSocials]);

  // Check for Instagram connection success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('instagram') === 'connected') {
      setSuccess('Instagram connected successfully!');
      // Remove query param from URL
      window.history.replaceState({}, '', window.location.pathname);
      // Reload profile to get updated Instagram data
      if (user?.uid) {
        loadProfile();
      }
    }
  }, [user]);

  const handleProfileImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setProfileImageFile(file);
    setError(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };


  if (userDocLoading || loading) {
    return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </DashboardShell>
    );
  }

  if (!user) {
    return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="max-w-xl mx-auto">
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Please sign in</h2>
            <p className="mt-2 text-sm text-slate-600">
              Sign in to edit your profile.
            </p>
            <button
              type="button"
              onClick={() => onNavigate('/login', null, { replace: true })}
              className="mt-4 inline-flex items-center rounded-md bg-[#1B9DA2] px-4 py-2 text-sm font-medium text-white"
            >
              Go to login
            </button>
          </div>
        </div>
      </DashboardShell>
    );
  }

  if (userDocError) {
    return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="max-w-xl mx-auto">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <h2 className="text-lg font-semibold text-red-700">We couldn’t load your profile</h2>
            <p className="mt-2 text-sm text-red-600">{userDocError}</p>
            <button
              type="button"
              onClick={() => onNavigate('/onboarding/step-1', null, { replace: true })}
              className="mt-4 inline-flex items-center rounded-md bg-[#1B9DA2] px-4 py-2 text-sm font-medium text-white"
            >
              Restart onboarding
            </button>
          </div>
        </div>
      </DashboardShell>
    );
  }

  const onboardingComplete = userDoc?.onboardingStatus === 'completed' || userDoc?.onboardingCompleted === true;
  if (userDoc && !onboardingComplete) {
    return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="max-w-xl mx-auto">
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Profile incomplete</h2>
            <p className="mt-2 text-sm text-slate-600">
              Finish onboarding to complete your profile.
            </p>
            <button
              type="button"
              onClick={() => onNavigate('/onboarding/step-1', null, { replace: true })}
              className="mt-4 inline-flex items-center rounded-md bg-[#1B9DA2] px-4 py-2 text-sm font-medium text-white"
            >
              Continue onboarding
            </button>
          </div>
        </div>
      </DashboardShell>
    );
  }

  // Visibility logic for optional sections
  const sectionsEnabled = formData.sectionsEnabled || {};
  
  const hasData = {
    platformPerformance: !!(formData.platformStats && Object.keys(formData.platformStats).length > 0),
    audience: !!formData.audience && Object.keys(formData.audience).length > 0,
    brandPartners: Array.isArray(formData.brandPartners) && formData.brandPartners.length > 0,
    collaborations: Array.isArray(formData.collaborations) && formData.collaborations.length > 0,
    featuredPosts: Array.isArray(formData.featuredPosts) && formData.featuredPosts.length > 0,
  };

  // Default: hidden unless user explicitly enables. Exception: Platform Performance is default-on.
  function isSectionVisible(key) {
    const explicit = sectionsEnabled[key];
    if (key === "platformPerformance") return explicit !== false; // default visible
    if (typeof explicit === "boolean") return explicit;
    return false; // default: hidden until user chooses to show
  }

  const showPlatformPerformance = isSectionVisible("platformPerformance");
  const showAudience = isSectionVisible("audience");
  const showBrandPartners = isSectionVisible("brandPartners");
  const showCollaborations = isSectionVisible("collaborations");
  const showFeaturedPosts = isSectionVisible("featuredPosts");

  // Username helper
  const username = formData?.username || formData?.identity?.handle || userDoc?.username || userDoc?.usernameLower || "";
  const isOwner = true; // This is the edit page, so user is always the owner

  return (
    <DashboardShell onNavigate={onNavigate}>
      <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Left column: Title + Description */}
              <div className="flex flex-col space-y-2 md:space-y-3">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Professional Media Kit</h1>
                <p className="text-gray-600">
                  Build your professional creator media kit. Share your link to showcase your work to brands and collaborators.
                </p>
              </div>
              {/* Right column: Action Buttons */}
              {isOwner && (
                <div className="flex flex-row items-center gap-3 md:justify-end">
                  {/* Add sections button */}
                  <button
                    type="button"
                    onClick={() => setShowSectionsModal(true)}
                    className="inline-flex items-center rounded-md bg-[#7842D6] px-4 py-2 text-xs md:text-sm font-semibold text-white shadow-sm hover:bg-[#5c30ac] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7842D6] transition-colors"
                  >
                    Add sections
                  </button>
                  {/* Share media kit button */}
                  {username && (
                    <ShareMediaKitButton 
                      username={username} 
                      variant="success"
                      size="md"
                      showSubtext={false}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Daily limit banner first so it's visible when refresh was skipped */}
            {rateLimitBanner && (
              <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">Daily refresh limit reached</p>
                  <p className="mt-1 text-sm">
                    You can refresh TikTok stats up to 3 times per day.
                    {rateLimitBanner.nextAllowedAtISO
                      ? ` Try again after ${new Date(rateLimitBanner.nextAllowedAtISO).toLocaleString()}.`
                      : ' Try again tomorrow.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setRateLimitBanner(null)}
                  className="flex-shrink-0 text-amber-600 hover:text-amber-800 font-medium"
                  aria-label="Dismiss"
                >
                  Dismiss
                </button>
              </div>
            )}
            {/* Error/Success Messages */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
                {success}
              </div>
            )}

            {/* Identity Section - New hero-style component with per-field editing */}
            <div id="identity">
            <IdentitySection
              identity={{
                // Merge identity object with fallback values from top-level fields
                displayName: formData.identity?.displayName || formData.displayName || '',
                handle: formData.identity?.handle || formData.username || '',
                creatorTitle: formData.identity?.creatorTitle || formData.creatorTitle || '',
                subtitle: formData.identity?.subtitle || (Array.isArray(formData.niche) ? formData.niche.join(', ') : formData.niche || ''),
                niches: Array.isArray(formData.niche) ? formData.niche.filter(Boolean) : (formData.niche ? [formData.niche] : []),
                location: formData.identity?.location || formData.location || '',
                primaryPlatforms: formData.identity?.primaryPlatforms || formData.platforms?.map(p => p.platformName) || [],
                // Deprecated: totalFollowersOverride is no longer used - total followers are computed from platformStats
                // totalFollowersOverride: formData.identity?.totalFollowersOverride ?? formData.stats?.totalFollowersOverride,
              }}
              platformStats={formData.platformStats || {}}
              socialAccounts={formData.socialAccounts || {}}
              isOwner={true}
              userId={user?.uid} // Pass userId for username claiming
              avatarUrl={(() => {
                const url = profileImagePreview || formData.photoURL || formData.profileImageUrl || formData.avatarUrl || '';
                console.log('[ProfileEditor] Computing avatarUrl prop:', {
                  profileImagePreview,
                  formDataPhotoURL: formData.photoURL,
                  formDataProfileImageUrl: formData.profileImageUrl,
                  formDataAvatarUrl: formData.avatarUrl,
                  finalUrl: url,
                });
                return url;
              })()}
              bannerImageUrl={formData.bannerImageUrl || ''}
              contactEmail={formData.contactEmail || ''}
              socialLinks={formData.socialLinks || {}}
              userDoc={userDoc}
              onSocialsChange={handleSocialsChange}
              profile={{
                ...formData,
                instagram: formData.instagram, // Pass Instagram integration data
              }}
              onAvatarChange={async (file) => {
                if (!user?.uid) {
                  console.error('[ProfileEditor] No user UID available');
                  return;
                }
                try {
                  console.log('[ProfileEditor] Starting avatar upload...', { file, userId: user.uid });
                  setError(null);
                  setSuccess(null);
                  
                  console.log('[ProfileEditor] Uploading image to Firebase Storage...');
                  const url = await uploadUserImage(file, user.uid, 'profile');
                  console.log('[ProfileEditor] Image uploaded successfully, URL:', url);
                  
                  console.log('[ProfileEditor] Updating local state...');
                  setFormData((prev) => {
                    const updated = {
                      ...prev,
                      profileImageUrl: url,
                      photoURL: url,
                      avatarUrl: url,
                    };
                    console.log('[ProfileEditor] FormData updated:', { 
                      profileImageUrl: updated.profileImageUrl,
                      photoURL: updated.photoURL,
                      avatarUrl: updated.avatarUrl 
                    });
                    return updated;
                  });
                  setProfileImagePreview(url);
                  console.log('[ProfileEditor] ProfileImagePreview set to:', url);
                  
                  console.log('[ProfileEditor] Saving to Firestore...');
                  await createOrUpdateInfluencerProfile(user.uid, {
                    profileImageUrl: url,
                    photoURL: url,
                    avatarUrl: url,
                  });
                  await setDoc(
                    doc(db, 'users', user.uid),
                    { photoURL: url },
                    { merge: true }
                  );
                  await triggerProfilePropagation({
                    uid: user.uid,
                    getToken: () =>
                      user && typeof user.getIdToken === 'function'
                        ? user.getIdToken()
                        : Promise.resolve(null),
                  });
                  console.log('[ProfileEditor] Firestore update completed');
                  
                  setSuccess('Profile photo updated!');
                  setTimeout(() => setSuccess(null), 2000);
                  console.log('[ProfileEditor] Avatar upload completed successfully');
                } catch (err) {
                  const extension = file?.name?.split('.').pop()?.toLowerCase() || 'jpg';
                  const uploadPath = `uploads/${user.uid}/profile.${extension}`;
                  const uiError = logStorageError(err, {
                    uid: user.uid,
                    uploadPath,
                    projectId: import.meta.env?.VITE_FIREBASE_PROJECT_ID,
                    storageBucketFromConfig: import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET,
                    fileType: file?.type ?? undefined,
                    fileSizeBytes: file?.size,
                    flow: 'profile-editor-avatar',
                  });
                  setError(`Failed to update profile photo: ${uiError}`);
                  throw err;
                }
              }}
              onAvatarRemove={async () => {
                if (!user?.uid) return;
                try {
                  setError(null);
                  setSuccess(null);
                  setFormData((prev) => ({
                    ...prev,
                    profileImageUrl: '',
                    photoURL: null,
                    avatarUrl: null,
                  }));
                  setProfileImagePreview(null);
                  await createOrUpdateInfluencerProfile(user.uid, {
                    profileImageUrl: '',
                    photoURL: null,
                    avatarUrl: null,
                  });
                  await setDoc(
                    doc(db, 'users', user.uid),
                    { photoURL: null },
                    { merge: true }
                  );
                  await triggerProfilePropagation({
                    uid: user.uid,
                    getToken: () =>
                      user && typeof user.getIdToken === 'function'
                        ? user.getIdToken()
                        : Promise.resolve(null),
                  });
                  setSuccess('Profile photo removed.');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error removing avatar:', err);
                  setError(`Failed to remove profile photo: ${err.message || err}`);
                }
              }}
              onBannerChange={async (file) => {
                if (!user?.uid) return;
                try {
                  setError(null);
                  setSuccess(null);
                  const url = await uploadUserImage(file, user.uid, 'banner');
                  setFormData((prev) => ({
                    ...prev,
                    bannerImageUrl: url,
                  }));
                  await createOrUpdateInfluencerProfile(user.uid, {
                    bannerImageUrl: url,
                  });
                  setSuccess('Banner image updated!');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  const extension = file?.name?.split('.').pop()?.toLowerCase() || 'jpg';
                  const uploadPath = `uploads/${user.uid}/banner.${extension}`;
                  const uiError = logStorageError(err, {
                    uid: user.uid,
                    uploadPath,
                    projectId: import.meta.env?.VITE_FIREBASE_PROJECT_ID,
                    storageBucketFromConfig: import.meta.env?.VITE_FIREBASE_STORAGE_BUCKET,
                    fileType: file?.type ?? undefined,
                    fileSizeBytes: file?.size,
                    flow: 'profile-editor-banner',
                  });
                  setError(`Failed to update banner image: ${uiError}`);
                  throw err;
                }
              }}
              onBannerRemove={async () => {
                if (!user?.uid) return;
                try {
                  setError(null);
                  setSuccess(null);
                  setFormData((prev) => ({
                    ...prev,
                    bannerImageUrl: '',
                  }));
                  await createOrUpdateInfluencerProfile(user.uid, {
                    bannerImageUrl: null,
                  });
                  setSuccess('Banner image removed.');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error removing banner:', err);
                  setError(`Failed to remove banner image: ${err.message || err}`);
                  throw err; // Re-throw so IdentitySection can handle it
                }
              }}
              onSave={async (partialIdentity) => {
                try {
                  // Update formData
                  if (partialIdentity.displayName !== undefined) {
                    setFormData(prev => ({ ...prev, displayName: partialIdentity.displayName }));
                  }
                  if (partialIdentity.handle !== undefined) {
                    setFormData(prev => ({ ...prev, username: partialIdentity.handle }));
                  }
                  if (partialIdentity.creatorTitle !== undefined) {
                    setFormData(prev => ({ ...prev, creatorTitle: partialIdentity.creatorTitle }));
                  }
                  if (partialIdentity.niches !== undefined) {
                    // Save niches as array
                    const nichesArray = Array.isArray(partialIdentity.niches) 
                      ? partialIdentity.niches.filter(Boolean)
                      : [];
                    setFormData(prev => ({ ...prev, niche: nichesArray }));
                  } else if (partialIdentity.subtitle !== undefined) {
                    // Legacy: handle subtitle as comma-separated niches
                    const nicheValue = partialIdentity.subtitle.trim() || '';
                    const nicheArray = nicheValue ? nicheValue.split(',').map(n => n.trim()).filter(Boolean) : [];
                    setFormData(prev => ({ ...prev, niche: nicheArray }));
                  }
                  if (partialIdentity.location !== undefined) {
                    setFormData(prev => ({ ...prev, location: partialIdentity.location }));
                  }
                  if (partialIdentity.primaryPlatforms !== undefined) {
                    // primaryPlatforms is now an array of normalized keys (e.g., ['tiktok', 'instagram'])
                    // We don't need to update the old platforms structure anymore
                    // Just update the identity.primaryPlatforms field
                  }
                  
                  // Build the update object - only include fields that were actually changed
                  const updateData = {};
                  
                  // Update top-level fields for backward compatibility
                  if (partialIdentity.displayName !== undefined) {
                    updateData.displayName = partialIdentity.displayName;
                  }
                  if (partialIdentity.handle !== undefined) {
                    // CRITICAL: Always sync handle to top-level username for public profile lookups
                    updateData.username = partialIdentity.handle.toLowerCase().trim();
                  }
                  if (partialIdentity.creatorTitle !== undefined) {
                    // Save creatorTitle at top level for easy access
                    updateData.creatorTitle = partialIdentity.creatorTitle || null;
                  }
                  if (partialIdentity.niches !== undefined) {
                    // Save niches as array
                    const nichesArray = Array.isArray(partialIdentity.niches) 
                      ? partialIdentity.niches.filter(Boolean)
                      : [];
                    updateData.niches = nichesArray;
                    // Also update legacy niche field for backward compatibility
                    updateData.niche = nichesArray;
                  } else if (partialIdentity.subtitle !== undefined) {
                    // Legacy: handle subtitle as comma-separated niches
                    const nicheValue = partialIdentity.subtitle.trim();
                    const nicheArray = nicheValue ? nicheValue.split(',').map(n => n.trim()).filter(Boolean) : [];
                    updateData.niches = nicheArray;
                    updateData.niche = nicheArray;
                  }
                  if (partialIdentity.location !== undefined) {
                    updateData.location = partialIdentity.location;
                  }
                  
                  // Update identity object (merge with existing)
                  const currentIdentity = formData.identity || {};
                  const updatedIdentity = {
                    ...currentIdentity,
                  };
                  
                  // Only add fields that are actually provided
                  if (partialIdentity.displayName !== undefined) {
                    updatedIdentity.displayName = partialIdentity.displayName;
                  }
                  if (partialIdentity.handle !== undefined) {
                    updatedIdentity.handle = partialIdentity.handle;
                  }
                  if (partialIdentity.creatorTitle !== undefined) {
                    updatedIdentity.creatorTitle = partialIdentity.creatorTitle || null;
                  }
                  if (partialIdentity.niches !== undefined) {
                    // Store niches array in identity (for backward compatibility, also keep subtitle)
                    const nichesArray = Array.isArray(partialIdentity.niches) 
                      ? partialIdentity.niches.filter(Boolean)
                      : [];
                    updatedIdentity.subtitle = nichesArray.length > 0 ? nichesArray.join(', ') : null;
                  } else if (partialIdentity.subtitle !== undefined) {
                    updatedIdentity.subtitle = partialIdentity.subtitle;
                  }
                  if (partialIdentity.location !== undefined) {
                    updatedIdentity.location = partialIdentity.location;
                  }
                  if (partialIdentity.primaryPlatforms !== undefined) {
                    updatedIdentity.primaryPlatforms = partialIdentity.primaryPlatforms;
                  }
                  // Deprecated: totalFollowersOverride is no longer used - total followers are computed from platformStats
                  // if (partialIdentity.totalFollowersOverride !== undefined) {
                  //   updatedIdentity.totalFollowersOverride = partialIdentity.totalFollowersOverride;
                  // }
                  
                  updateData.identity = updatedIdentity;
                  
                  // Handle contactEmail if provided
                  if (partialIdentity.contactEmail !== undefined) {
                    updateData.contactEmail = partialIdentity.contactEmail;
                  }
                  
                  // Handle socialLinks if provided
                  if (partialIdentity.socialLinks !== undefined) {
                    updateData.socialLinks = partialIdentity.socialLinks;
                    if (partialIdentity.socialLinks?.tiktok) {
                      updateData.socialConsent = { publicData: true };
                    }
                    if (!partialIdentity.socialLinks?.tiktok) {
                      updateData.socialAccounts = { ...(formData.socialAccounts || {}), tiktok: null };
                    }
                    const igUrl = partialIdentity.socialLinks?.instagram ?? (Array.isArray(partialIdentity.socialLinks) ? partialIdentity.socialLinks.find((l) => l?.platform === 'instagram')?.url : undefined);
                    const igHandleFromLink = igUrl ? (() => {
                      const t = String(igUrl).trim();
                      const m = t.match(/instagram\.com\/([^/?#]+)/i);
                      return m ? m[1].replace(/^@/, '').trim().toLowerCase() : t.replace(/^@/, '').trim().toLowerCase();
                    })() : null;
                    if (igHandleFromLink) {
                      updateData.socialAccounts = updateData.socialAccounts || { ...(formData.socialAccounts || {}) };
                      const existing = formData.socialAccounts?.instagram;
                      const existingHandleNorm = existing ? getInstagramHandleFromLink(existing.handle || existing.profileUrl) : null;
                      const instagramHandleChanged = !existing || existingHandleNorm !== igHandleFromLink;
                      updateData.socialAccounts.instagram = existing
                        ? { ...existing, handle: igHandleFromLink, profileUrl: `https://www.instagram.com/${igHandleFromLink}/`, pendingRefresh: instagramHandleChanged ? true : (existing.pendingRefresh ?? false) }
                        : { handle: igHandleFromLink, profileUrl: `https://www.instagram.com/${igHandleFromLink}/`, followersCount: null, lastRefreshedAt: null, pendingRefresh: true, derived: null, posts: [], rawRef: null };
                    } else if (igUrl === undefined || igUrl === null || igUrl === '') {
                      updateData.socialAccounts = updateData.socialAccounts || { ...(formData.socialAccounts || {}) };
                      updateData.socialAccounts.instagram = null;
                    }
                  }
                  
                  // Remove undefined values from updateData
                  Object.keys(updateData).forEach(key => {
                    if (updateData[key] === undefined) {
                      delete updateData[key];
                    }
                  });
                  
                  // Also clean nested identity object
                  if (updateData.identity) {
                    Object.keys(updateData.identity).forEach(key => {
                      if (updateData.identity[key] === undefined) {
                        delete updateData.identity[key];
                      }
                    });
                  }
                  
                  // Update formData
                  setFormData(prev => ({
                    ...prev,
                    identity: updatedIdentity,
                    ...(partialIdentity.displayName !== undefined && { displayName: partialIdentity.displayName }),
                    ...(partialIdentity.handle !== undefined && { username: partialIdentity.handle }),
                    ...(partialIdentity.creatorTitle !== undefined && { creatorTitle: partialIdentity.creatorTitle }),
                    ...(partialIdentity.niches !== undefined && { niche: Array.isArray(partialIdentity.niches) ? partialIdentity.niches.filter(Boolean) : [] }),
                    ...(partialIdentity.subtitle !== undefined && !partialIdentity.niches && { niche: partialIdentity.subtitle.trim() ? partialIdentity.subtitle.split(',').map(n => n.trim()).filter(Boolean) : [] }),
                    ...(partialIdentity.location !== undefined && { location: partialIdentity.location }),
                    ...(partialIdentity.contactEmail !== undefined && { contactEmail: partialIdentity.contactEmail }),
                    ...(partialIdentity.socialLinks !== undefined && { socialLinks: partialIdentity.socialLinks }),
                  }));
                  
                  // Save to Firestore
                  await createOrUpdateInfluencerProfile(user.uid, updateData);
                  await triggerProfilePropagation({
                    uid: user.uid,
                    getToken: () =>
                      user && typeof user.getIdToken === 'function'
                        ? user.getIdToken()
                        : Promise.resolve(null),
                  });

                  const prevLinks = formData.socialLinks || {};
                  const nextLinks = partialIdentity.socialLinks || {};
                  const tiktokAddedOrChanged = platformAddedOrChanged('tiktok', nextLinks, prevLinks);
                  const instagramAddedOrChanged = platformAddedOrChanged('instagram', nextLinks, prevLinks);

                  if (tiktokAddedOrChanged && instagramAddedOrChanged) {
                    setError('Please add or update one platform at a time. Save one, then add the other.');
                    return;
                  }

                  // Run only the scraper for the platform that was added or changed
                  if (tiktokAddedOrChanged) {
                    setScraperLoading(true);
                    setScraperDone(false);
                    setScraperPlatform('TikTok');
                    try {
                      const res = await fetch('/api/social/tiktok/refresh', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          tiktokHandleOrUrl: nextLinks.tiktok,
                          source: 'edit_profile',
                          reason: 'handle_change',
                          userId: user.uid,
                        }),
                      });
                      const body = res.ok ? await res.json().catch(() => ({})) : {};
                      if (res.ok && (body.reason === 'daily_limit' || (body.skipped && body.rateLimit))) {
                        setRateLimitBanner(body.rateLimit ? { nextAllowedAtISO: body.rateLimit.nextAllowedAtISO } : { nextAllowedAtISO: '' });
                        setDailyLimitModal({
                          open: true,
                          nextAllowedAtISO: body.rateLimit?.nextAllowedAtISO ?? null,
                          platform: 'TikTok',
                        });
                      }
                      if (res.ok && body.ok && !body.skipped) {
                        setSuccess('TikTok stats updated');
                        if (body.account || body.derived) {
                          setFormData((prev) => ({
                            ...prev,
                            socialAccounts: {
                              ...(prev.socialAccounts || {}),
                              tiktok: {
                                ...(prev.socialAccounts?.tiktok || {}),
                                handle: body.account?.handle ?? prev.socialAccounts?.tiktok?.handle,
                                profileUrl: body.account?.profileUrl ?? prev.socialAccounts?.tiktok?.profileUrl,
                                followersCount: body.account?.followersCount ?? prev.socialAccounts?.tiktok?.followersCount,
                                derived: body.derived ?? prev.socialAccounts?.tiktok?.derived,
                                lastRefreshedAt: new Date().toISOString(),
                              },
                            },
                          }));
                        }
                        // Do NOT refetch after success — avoids stale read overwriting newly added platform's stats
                      } else if (res.ok && body.skipped && body.reason === 'daily_limit') {
                        setSuccess('Saved.');
                        setTimeout(() => loadProfile({ fromServer: true }), 1200);
                      } else if (res.ok && body.skipped) {
                        const at = body.nextAllowedAt ? new Date(body.nextAllowedAt).toLocaleString() : 'later';
                        setSuccess(body.reason === 'cooldown_handle_changed' ? `Handle updated; next refresh at ${at}` : `TikTok stats can be refreshed again at ${at}`);
                        setTimeout(() => loadProfile({ fromServer: true }), 1200);
                      } else {
                        setSuccess(res.ok ? 'Saved. Refreshing stats…' : 'Saved handle, but refresh failed. Try again later.');
                        setTimeout(() => loadProfile({ fromServer: true }), 1200);
                        if (res.ok) setTimeout(() => loadProfile({ fromServer: true }), 2500);
                      }
                    } catch (tiktokErr) {
                      console.warn('[ProfileEdit] TikTok refresh failed (non-blocking):', tiktokErr);
                      setSuccess('Saved handle, but refresh failed. Try again later.');
                      setTimeout(() => loadProfile({ fromServer: true }), 1200);
                    } finally {
                      setScraperDone(true);
                    }
                  } else if (instagramAddedOrChanged) {
                    const igHandle = getInstagramHandleFromLink(nextLinks.instagram);
                    if (igHandle) {
                      setScraperLoading(true);
                      setScraperDone(false);
                      setScraperPlatform('Instagram');
                      try {
                        const res = await fetch('/api/social/instagram/refresh', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ handle: igHandle, userId: user.uid }),
                        });
                        const body = res.ok ? await res.json().catch(() => ({})) : {};
                        if (res.ok && body.ok) {
                          setSuccess('Instagram stats updated');
                          setFormData((prev) => ({
                            ...prev,
                            socialAccounts: {
                              ...(prev.socialAccounts || {}),
                              instagram: body.instagram ?? prev.socialAccounts?.instagram,
                            },
                          }));
                          // Do NOT refetch after success — avoids stale read overwriting metrics
                        } else if (res.status === 429 && body.error === 'DAILY_LIMIT') {
                          setSuccess('Instagram refresh limit reached for today.');
                          setRateLimitBanner(body.nextAllowedAtISO ? { nextAllowedAtISO: body.nextAllowedAtISO } : { nextAllowedAtISO: '' });
                          setDailyLimitModal({
                            open: true,
                            nextAllowedAtISO: body.nextAllowedAtISO ?? null,
                            platform: 'Instagram',
                          });
                          setTimeout(() => loadProfile({ fromServer: true }), 1200);
                        } else {
                          setSuccess(res.status === 429 ? 'Instagram refresh limit reached for today.' : 'Saved. Instagram refresh failed—try again later.');
                          setTimeout(() => loadProfile({ fromServer: true }), 1200);
                        }
                      } catch (igErr) {
                        console.warn('[ProfileEdit] Instagram refresh failed (non-blocking):', igErr);
                        setSuccess('Saved. Instagram refresh failed—try again later.');
                        setTimeout(() => loadProfile({ fromServer: true }), 1200);
                      } finally {
                        setScraperDone(true);
                      }
                    } else {
                      setSuccess('Identity updated!');
                      loadProfile();
                    }
                  } else {
                    setSuccess('Identity updated!');
                    loadProfile();
                  }
                  setTimeout(() => setSuccess(null), 4000);
                } catch (err) {
                  console.error('Error saving identity:', err);
                  setError(`Failed to save identity: ${err.message || err}`);
                }
              }}
            />
            </div>
            
            {/* Username info note (since username is in IdentitySection now) */}
            <div className="mt-2 mb-6">
              <p className="text-xs text-slate-500">
                Your media kit will be available at{' '}
                <span className="font-mono text-slate-700">
                  {typeof window !== "undefined" ? window.location.host : "influencehub.net"}/u/{username || 'username'}
                </span>
              </p>
            </div>

            {/* 2️⃣ About Me */}
            <div id="about">
            <ProfileBioSectionBeacons
              profile={formData}
              isOwner={true}
              onBioChange={async (bio) => {
                try {
                  setFormData(prev => ({ ...prev, bio }));
                  await createOrUpdateInfluencerProfile(user.uid, { bio });
                  setSuccess('About Me updated!');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error updating bio:', err);
                  setError(`Failed to save About Me: ${err.message || err}`);
                }
              }}
            />
            </div>

            {/* 3️⃣ Platform Performance */}
            {showPlatformPerformance && (
            <div id="platform-performance">
            <PlatformPerformanceSection
              platformStats={formData.platformStats || {}}
              primaryPlatforms={getPlatformsFromProfile({ socialLinks: formData.socialLinks, socialAccounts: formData.socialAccounts })}
              isOwner={true}
              socialAccounts={formData.socialAccounts}
              socialLinks={formData.socialLinks}
              onSavePlatformStats={async (updatedPlatformStats) => {
                try {
                  // Clean the stats - remove undefined values and ensure proper structure
                  // The updatedPlatformStats should already be cleaned by PlatformPerformanceSection
                  // Just ensure we don't pass undefined values
                  const cleanedStats = {};
                  Object.keys(updatedPlatformStats).forEach((key) => {
                    const platformStat = updatedPlatformStats[key];
                    if (platformStat && typeof platformStat === 'object') {
                      const cleanedPlatformStat = {};
                      Object.keys(platformStat).forEach((statKey) => {
                        const value = platformStat[statKey];
                        // Only include defined, non-null, non-empty values
                        if (value !== undefined && value !== null && value !== '') {
                          cleanedPlatformStat[statKey] = value;
                        }
                      });
                      // Only add platform if it has at least one stat
                      if (Object.keys(cleanedPlatformStat).length > 0) {
                        cleanedStats[key] = cleanedPlatformStat;
                      }
                    }
                  });
                  
                  // Update formData
                  setFormData(prev => ({
                    ...prev,
                    platformStats: {
                      ...(prev.platformStats || {}),
                      ...cleanedStats,
                    },
                  }));
                  
                  // Save to Firestore - merge with existing platformStats
                  const currentPlatformStats = formData.platformStats || {};
                  const mergedPlatformStats = {
                    ...currentPlatformStats,
                    ...cleanedStats,
                  };
                  
                  // Remove undefined values from merged stats
                  Object.keys(mergedPlatformStats).forEach((key) => {
                    if (mergedPlatformStats[key] && typeof mergedPlatformStats[key] === 'object') {
                      Object.keys(mergedPlatformStats[key]).forEach((statKey) => {
                        if (mergedPlatformStats[key][statKey] === undefined) {
                          delete mergedPlatformStats[key][statKey];
                        }
                      });
                      // Remove platform if it has no stats
                      if (Object.keys(mergedPlatformStats[key]).length === 0) {
                        delete mergedPlatformStats[key];
                      }
                    }
                  });
                  
                  await createOrUpdateInfluencerProfile(user.uid, {
                    platformStats: mergedPlatformStats,
                  });
                  
                  setSuccess('Platform stats updated!');
                  setTimeout(() => setSuccess(null), 2000);
                  setError(null);
                } catch (err) {
                  console.error('Error saving platform stats:', err);
                  setError(`Failed to save platform stats: ${err.message || err}`);
                  setSuccess(null);
                }
              }}
            />
            </div>
            )}

            {/* 4️⃣ Audience & Demographics */}
            {showAudience && (
            <div id="audience">
            <AudienceSection
              audience={formData.audience}
              isOwner={true}
              onSaveAudience={async (partialAudience) => {
                try {
                  setError(null);
                  
                  // Helper function to remove undefined values recursively
                  const removeUndefined = (obj) => {
                    if (obj === null || typeof obj !== 'object') {
                      return obj;
                    }
                    if (Array.isArray(obj)) {
                      return obj.map(removeUndefined).filter(item => item !== undefined);
                    }
                    const cleaned = {};
                    Object.keys(obj).forEach(key => {
                      const value = obj[key];
                      if (value !== undefined) {
                        cleaned[key] = removeUndefined(value);
                      }
                    });
                    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
                  };
                  
                  // Merge with existing audience and remove undefined values
                  const mergedAudience = {
                    ...(formData.audience || {}),
                    ...partialAudience,
                  };
                  
                  // Clean nested objects (gender, ageBrackets, etc.)
                  if (mergedAudience.gender) {
                    mergedAudience.gender = removeUndefined(mergedAudience.gender);
                    if (!mergedAudience.gender || Object.keys(mergedAudience.gender).length === 0) {
                      delete mergedAudience.gender;
                    }
                  }
                  
                  if (mergedAudience.ageBrackets) {
                    mergedAudience.ageBrackets = removeUndefined(mergedAudience.ageBrackets);
                    if (!mergedAudience.ageBrackets || Object.keys(mergedAudience.ageBrackets).length === 0) {
                      delete mergedAudience.ageBrackets;
                    }
                  }
                  
                  // Clean arrays (topCountries, topCities)
                  if (mergedAudience.topCountries) {
                    mergedAudience.topCountries = mergedAudience.topCountries
                      .map(country => removeUndefined(country))
                      .filter(country => country && country.name);
                    if (mergedAudience.topCountries.length === 0) {
                      delete mergedAudience.topCountries;
                    }
                  }
                  
                  if (mergedAudience.topCities) {
                    mergedAudience.topCities = mergedAudience.topCities
                      .map(city => removeUndefined(city))
                      .filter(city => city && city.name);
                    if (mergedAudience.topCities.length === 0) {
                      delete mergedAudience.topCities;
                    }
                  }
                  
                  // Remove undefined top-level fields
                  Object.keys(mergedAudience).forEach(key => {
                    if (mergedAudience[key] === undefined) {
                      delete mergedAudience[key];
                    }
                  });
                  
                  // Update formData
                  setFormData(prev => ({
                    ...prev,
                    audience: mergedAudience,
                  }));
                  
                  // Save to Firestore
                  await createOrUpdateInfluencerProfile(user.uid, {
                    audience: mergedAudience,
                  });
                  
                  setSuccess('Audience data updated!');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error saving audience:', err);
                  setError(`Failed to save audience: ${err.message || err}`);
                  setSuccess(null);
                }
              }}
            />
            </div>
            )}

            {/* 5️⃣ Brand Partners */}
            {showBrandPartners && (
            <BrandPartnersSection
              brandPartners={formData.brandPartners || []}
              isOwner={true}
              onSaveBrandPartners={async (partners) => {
                try {
                  setError(null);
                  // Clean partners - remove undefined values and ensure all required fields exist
                  const cleanedPartners = (partners || []).map((p) => {
                    const cleaned = {
                      id: p.id || String(Date.now() + Math.random()),
                      name: (p.name || '').trim(),
                    };
                    // Only include logoUrl if it exists and is not empty
                    if (p.logoUrl && typeof p.logoUrl === 'string' && p.logoUrl.trim()) {
                      cleaned.logoUrl = p.logoUrl.trim();
                    }
                    return cleaned;
                  }).filter((p) => p.name && p.name.length > 0);
                  
                  // Update formData first
                  setFormData((prev) => ({ ...prev, brandPartners: cleanedPartners }));
                  // Save to Firestore
                  await createOrUpdateInfluencerProfile(user.uid, { brandPartners: cleanedPartners });
                  setSuccess('Brand partners updated!');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error saving brand partners:', err);
                  setError(`Failed to save brand partners: ${err.message || err}`);
                  setSuccess(null);
                }
              }}
            />
            )}

            {/* 6️⃣ Collaboration Highlights */}
            {showCollaborations && (
            <CollaborationHighlightsSection
              collaborations={formData.collaborations || []}
              isOwner={true}
              onSaveCollaborations={async (collabs) => {
                try {
                  setError(null);
                  // Update formData first
                  setFormData((prev) => ({ ...prev, collaborations: collabs }));
                  // Save to Firestore
                  await createOrUpdateInfluencerProfile(user.uid, { collaborations: collabs });
                  setSuccess('Collaborations updated!');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error saving collaborations:', err);
                  setError(`Failed to save collaborations: ${err.message || err}`);
                  setSuccess(null);
                }
              }}
            />
            )}

            {/* 7️⃣ Featured Posts */}
            {showFeaturedPosts && (
            <FeaturedPostsSection
              featuredPosts={formData.featuredPosts || []}
              profile={formData}
              userDoc={userDoc}
              isOwner={true}
              onSaveFeaturedPosts={async (posts) => {
                try {
                  setError(null);
                  // Update formData first
                  setFormData((prev) => ({ ...prev, featuredPosts: posts }));
                  // Save to Firestore
                  await createOrUpdateInfluencerProfile(user.uid, { featuredPosts: posts });
                  setSuccess('Featured posts updated!');
                  setTimeout(() => setSuccess(null), 2000);
                } catch (err) {
                  console.error('Error saving featured posts:', err);
                  setError(`Failed to save featured posts: ${err.message || err}`);
                  setSuccess(null);
                }
              }}
            />
            )}

            <p className="text-xs text-slate-500 mt-8 text-center">
              Note: Each section above has its own Edit button. Changes in those sections save automatically.
            </p>
          </div>

      {/* Sections Modal */}
      <SectionsModal
        isOpen={showSectionsModal}
        onClose={() => setShowSectionsModal(false)}
        sectionsEnabled={sectionsEnabled}
        hasData={hasData}
        onUpdateSectionsEnabled={async (next) => {
          try {
            setFormData((prev) => ({ ...prev, sectionsEnabled: next }));
            await createOrUpdateInfluencerProfile(user.uid, { sectionsEnabled: next });
            setSuccess('Sections updated!');
            setTimeout(() => setSuccess(null), 2000);
          } catch (err) {
            console.error('Error updating sections enabled:', err);
            setError(`Failed to update sections: ${err.message || err}`);
          }
        }}
      />

      <ScraperLoadingModal
        isOpen={scraperLoading}
        isDone={scraperDone}
        onClose={() => {
          setScraperLoading(false);
          setScraperDone(false);
          setScraperPlatform('');
        }}
        platformLabel={scraperPlatform}
      />

      <DailyLimitModal
        isOpen={dailyLimitModal.open}
        onClose={() => setDailyLimitModal((prev) => ({ ...prev, open: false }))}
        nextAllowedAtISO={dailyLimitModal.nextAllowedAtISO}
        platformLabel={dailyLimitModal.platform || 'this platform'}
      />
    </DashboardShell>
  );
};

export default ProfileEditor;
